import { useState, useEffect } from 'react';
import axios from 'axios';
import { Warehouse, LogOut, Check, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getItalianDateTime = () => {
  const now = new Date();
  const italianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const year = italianTime.getFullYear();
  const month = String(italianTime.getMonth() + 1).padStart(2, '0');
  const day = String(italianTime.getDate()).padStart(2, '0');
  const hours = String(italianTime.getHours()).padStart(2, '0');
  const minutes = String(italianTime.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const OperatoreCellaOut = () => {
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedBin, setSelectedBin] = useState(null);
  
  const [formData, setFormData] = useState({
    kg_uscita: '',
    data_uscita: getItalianDateTime()
  });

  const fetchBins = async () => {
    try {
      const response = await axios.get(`${API}/cella/bins`);
      setBins((response.data || []).filter(b => b.status === 'in_cella'));
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      setBins([]);
    }
  };

  useEffect(() => {
    fetchBins();
  }, []);

  const handleSelectBin = (bin) => {
    setSelectedBin(bin);
    setFormData({ 
      kg_uscita: bin.kg_attuali.toString(),
      data_uscita: getItalianDateTime()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBin) {
      toast.error('Seleziona un prodotto');
      return;
    }
    
    setLoading(true);
    
    try {
      // REGISTRA DIRETTAMENTE L'USCITA (senza approvazione)
      await axios.post(`${API}/cella/uscita`, {
        prodotto_sigla: selectedBin.prodotto_sigla,
        kg_uscita: parseFloat(formData.kg_uscita),
        data_uscita: new Date(formData.data_uscita).toISOString(),
        note: 'Uscita da operatore'
      });
      
      setSuccess(true);
      setSelectedBin(null);
      setFormData({ kg_uscita: '', data_uscita: getItalianDateTime() });
      
      setTimeout(() => {
        setSuccess(false);
        fetchBins();
      }, 3000);
      toast.success('Uscita registrata!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione uscita');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-24 h-24 bg-[#EF4444] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Uscita Registrata!</h2>
          <p className="text-white/60">Il movimento è stato registrato</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] p-4" data-testid="operatore-cella-out-page">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6 pt-8">
          <div className="w-16 h-16 bg-[#EF4444]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Warehouse className="w-8 h-8 text-[#EF4444]" />
          </div>
          <h1 className="text-2xl font-bold text-white">CELLA OUT</h1>
          <p className="text-white/50">Registra uscita da cella</p>
        </div>

        {/* Selezione Prodotto */}
        {!selectedBin ? (
          <div className="space-y-3">
            <p className="text-white/70 text-sm font-medium mb-2">Seleziona il prodotto da scaricare:</p>
            
            {bins.length === 0 ? (
              <div className="bg-white/10 border border-white/20 rounded-2xl p-8 text-center">
                <Package className="w-12 h-12 text-white/30 mx-auto mb-3" />
                <p className="text-white/50">Nessun prodotto in cella</p>
              </div>
            ) : (
              bins.map((bin) => (
                <button
                  key={bin.id}
                  onClick={() => handleSelectBin(bin)}
                  className="w-full bg-white/10 border border-white/20 rounded-2xl p-4 text-left hover:bg-white/20 transition-colors"
                  data-testid={`select-bin-${bin.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-sm">
                          {bin.prodotto_sigla}
                        </span>
                        <span className="text-white font-medium">{bin.prodotto_nome}</span>
                      </div>
                      <p className="text-sm text-white/50">{bin.fornitore}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#22C55E] text-xl">{bin.kg_attuali} kg</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        ) : (
          /* Form Uscita */
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-1 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded font-mono font-bold text-sm">
                      {selectedBin.prodotto_sigla}
                    </span>
                    <span className="text-white font-medium">{selectedBin.prodotto_nome}</span>
                  </div>
                  <p className="text-sm text-white/50">{selectedBin.fornitore}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedBin(null)}
                  className="text-[#8B5CF6] text-sm hover:underline"
                >
                  Cambia
                </button>
              </div>
              <div className="mt-2 pt-2 border-t border-white/10">
                <p className="text-sm text-white/70">Disponibili: <span className="font-bold text-[#22C55E]">{selectedBin.kg_attuali} kg</span></p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Quantità in Uscita (KG) *</label>
                <input
                  type="number"
                  step="0.01"
                  max={selectedBin.kg_attuali}
                  value={formData.kg_uscita}
                  onChange={(e) => setFormData({ ...formData, kg_uscita: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-2xl font-bold text-center"
                  placeholder="0.00"
                  required
                  data-testid="cella-out-kg-input"
                />
                <p className="text-center text-xs text-white/50 mt-2">
                  Max: {selectedBin.kg_attuali} kg
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">Data/Ora Uscita</label>
                <input
                  type="datetime-local"
                  value={formData.data_uscita}
                  onChange={(e) => setFormData({ ...formData, data_uscita: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#EF4444] text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-[#EF4444]/80 transition-colors disabled:opacity-50"
              data-testid="cella-out-submit-btn"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogOut className="w-6 h-6" />
                  REGISTRA USCITA
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default OperatoreCellaOut;
